import { useEffect, useState, Fragment } from "react";
import ComboCode from "./ComboCode";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, ClipboardPaste, Info, Download, Upload, FileSpreadsheet, Image as ImageIcon, Printer } from "lucide-react";
import { SkuImage, useShowPhoto } from "@/components/SkuImage";
import { SkuLabelPrintDialog } from "@/components/SkuLabelPrint";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const empty = { sku: "", name: "", description: "", rack_code: "", unit: "pcs", image_url: "", is_packaging: false, packaging_allocation: false, packaging_product_sku_ids: [] };
const PASTE_HEADERS = ["sku", "name", "description", "rack_code", "unit"];

function parseTSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  // Detect header line
  const first = lines[0].split(/\t|,/).map(s => s.trim().toLowerCase());
  const headerLike = PASTE_HEADERS.some(h => first.includes(h));
  const startIdx = headerLike ? 1 : 0;
  const cols = headerLike ? first : PASTE_HEADERS;
  const idx = (name) => cols.indexOf(name);
  return lines.slice(startIdx).map(l => {
    const parts = l.split(/\t|,/).map(s => s.trim());
    return {
      sku: parts[idx("sku")] || parts[0] || "",
      name: parts[idx("name")] || parts[1] || "",
      description: parts[idx("description")] || parts[2] || "",
      rack_code: parts[idx("rack_code")] || parts[3] || "",
      unit: parts[idx("unit")] || parts[4] || "pcs",
    };
  });
}

export default function MasterSKU() {
  const { user, can } = useAuth();
  const { density, setDensity } = useUiPrefs("master-sku");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [expanded, setExpanded] = useState({});
  const [showPhoto, ShowPhotoToggle] = useShowPhoto("master-sku");
  const [skuTab, setSkuTab] = useState("products");
  const [pkgSearch, setPkgSearch] = useState("");
  const [pkgResults, setPkgResults] = useState([]);
  const [pkgNames, setPkgNames] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeletePw, setBulkDeletePw] = useState("");
  const [bulkDeleteXlsxOpen, setBulkDeleteXlsxOpen] = useState(false);
  const [bulkDeleteXlsxPw, setBulkDeleteXlsxPw] = useState("");
  const [bulkDeleteXlsxFile, setBulkDeleteXlsxFile] = useState(null);
  const [pageSize, setPageSize] = useState(() => {
    const s = parseInt(localStorage.getItem("master-sku-page-size") || "50", 10);
    return [50, 100, 500].includes(s) ? s : 50;
  });
  const [page, setPage] = useState(1);
  const [showDims, setShowDims] = useState(() => localStorage.getItem("master-sku-show-dims") === "1");
  const toggleDims = (v) => { setShowDims(v); localStorage.setItem("master-sku-show-dims", v ? "1" : "0"); };
  const [labelSku, setLabelSku] = useState(null);
  const [transitDrill, setTransitDrill] = useState(null);
  const previewRows = pasteText ? parseTSV(pasteText) : [];

  const openTransit = async (r) => {
    try {
      setTransitDrill({ sku: r.sku, name: r.name, loading: true, items: [], total_qty: r.in_transit_stock ?? 0 });
      const { data } = await api.get(`/skus/${r.id}/in-transit`);
      setTransitDrill({ sku: r.sku, name: r.name, loading: false, items: data.items, total_qty: data.total_qty });
    } catch { toast.error("Failed to load in-transit detail"); setTransitDrill(null); }
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const viewRows = rows.filter(r => skuTab === "packaging" ? r.is_packaging : !r.is_packaging);
  const pagedRows = viewRows.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [debouncedQ, pageSize, rows.length]);
  const changePageSize = (n) => {
    setPageSize(n);
    localStorage.setItem("master-sku-page-size", String(n));
  };

  const toggleSel = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllSel = () => {
    setSelected(prev => {
      const pageIds = pagedRows.map(r => r.id);
      const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSel = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (!bulkDeletePw) return toast.error("Password required");
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/skus/bulk-delete", { ids, password: bulkDeletePw });
      toast.success(`Deleted ${data.deleted} SKU${data.deleted === 1 ? "" : "s"}`);
      setBulkDeleteOpen(false); setBulkDeletePw(""); clearSel(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkDeleteXlsx = async () => {
    if (!bulkDeleteXlsxFile) return toast.error("Choose an Excel file first");
    if (!bulkDeleteXlsxPw) return toast.error("Password required");
    try {
      const fd = new FormData();
      fd.append("file", bulkDeleteXlsxFile);
      fd.append("password", bulkDeleteXlsxPw);
      const { data } = await api.post("/skus/bulk-delete-xlsx", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Deleted ${data.deleted} of ${data.requested} SKU${data.deleted === 1 ? "" : "s"}`);
      if (data.not_found?.length) {
        data.not_found.slice(0, 5).forEach(c => toast.warning(`Not found: ${c}`));
        if (data.not_found.length > 5) toast.warning(`… and ${data.not_found.length - 5} more not found`);
      }
      setBulkDeleteXlsxOpen(false); setBulkDeleteXlsxPw(""); setBulkDeleteXlsxFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = () => api.get(`/skus${debouncedQ ? `?q=${encodeURIComponent(debouncedQ)}` : ""}`).then(r => setRows(r.data));

  const searchPkg = async (term) => {
    if (!term || term.length < 2) { setPkgResults([]); return; }
    try {
      const { data } = await api.get(`/skus?q=${encodeURIComponent(term)}`);
      setPkgResults(data.filter(s => !s.is_packaging).slice(0, 20).map(s => ({ id: s.id, sku: s.sku, name: s.name })));
    } catch { setPkgResults([]); }
  };
  const addPkgProduct = (p) => {
    const ids = new Set(form.packaging_product_sku_ids || []);
    ids.add(p.id);
    setForm({ ...form, packaging_product_sku_ids: Array.from(ids) });
    setPkgNames({ ...pkgNames, [p.id]: p.sku });
    setPkgSearch(""); setPkgResults([]);
  };
  const removePkgProduct = (pid) => setForm({ ...form, packaging_product_sku_ids: (form.packaging_product_sku_ids || []).filter(x => x !== pid) });
  const runSearch = () => setDebouncedQ(q.trim());
  const clearSearch = () => { setQ(""); setDebouncedQ(""); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debouncedQ]);

  const save = async () => {
    try {
      if (editing) await api.put(`/skus/${editing.id}`, form);
      else await api.post("/skus", form);
      toast.success(editing ? "SKU updated" : "SKU added");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this SKU?")) return;
    try { await api.delete(`/skus/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (r) => { setEditing(r); setForm(r); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const saveBalance = async (id, value) => {
    try {
      await api.patch(`/skus/${id}/balance-area`, { balance_stock_area: value });
      toast.success("Balance area saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkImport = async () => {
    if (!previewRows.length) return toast.error("No rows to import");
    try {
      const { data } = await api.post("/skus/bulk", { rows: previewRows });
      toast.success(`Imported ${data.created.length} SKUs${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors.length) data.errors.slice(0, 3).forEach(e => toast.warning(`Row ${e.row} (${e.sku}): ${e.error}`));
      setPasteOpen(false); setPasteText(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${(await import("@/lib/api")).API}${path}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed: " + (e.message || "")); }
  };

  const uploadUpsert = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/skus/bulk-upsert", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.created} · Updated ${data.updated}${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadStockAdjust = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/skus/stock-adjust", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Deducted ${data.applied} SKUs · est. value RM ${data.estimated_value_deducted_rm?.toFixed(2) ?? "0"}${data.errors?.length ? ` · ${data.errors.length} skipped` : ""}`);
      if (data.errors?.length) data.errors.slice(0,3).forEach(e => toast.warning(`Row ${e.row} (${e.sku}): ${e.error}`));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadRackAdjust = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/skus/rack-adjust", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated rack codes on ${data.updated} SKUs${data.missing?.length ? ` · ${data.missing.length} SKUs not found` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Master data</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Master SKU Sheet</h1>
          <p className="text-slate-500 mt-1 text-sm">Every item you buy, indexed by SKU with rack code.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DensityToggle density={density} setDensity={setDensity} />
          {ShowPhotoToggle}
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <Checkbox checked={showDims} onCheckedChange={toggleDims} data-testid="sku-show-dims-toggle" />
            <span>Show Dimensions</span>
          </label>
          <div className="relative w-full sm:w-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="sku-search-input" placeholder="Search SKU, name, rack…"
              value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") runSearch(); }} className="pl-9 w-full sm:w-72" />
          </div>
          <Button data-testid="sku-search-button" onClick={runSearch} className="bg-slate-900 hover:bg-slate-800">
            <Search className="w-4 h-4 mr-1.5" />Search
          </Button>
          {debouncedQ && (
            <Button variant="ghost" data-testid="sku-search-clear" onClick={clearSearch} className="text-slate-500">
              Clear
            </Button>
          )}
          {can("sku:bulk_update") && selected.size > 0 && (
            <Button variant="destructive" onClick={()=>setBulkDeleteOpen(true)} data-testid="sku-bulk-delete-button" className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete Selected ({selected.size})
            </Button>
          )}
          {can("sku:bulk_update") && (
            <Button variant="outline" onClick={()=>setBulkDeleteXlsxOpen(true)} data-testid="sku-bulk-delete-xlsx-button" className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <Upload className="w-4 h-4 mr-1.5" />Delete via Excel
            </Button>
          )}
          {(can("sku:export") || can("sku:in_transit_export") || can("sku:template") || can("sku:upload") || can("sku:edit_initial_qty") || can("sku:edit_rack")) && (
            <>
            {can("sku:export") && <Button variant="outline" onClick={() => downloadFile("/skus/export.xlsx", `master-sku-${new Date().toISOString().slice(0,10)}.xlsx`)} data-testid="sku-export-button">
              <Download className="w-4 h-4 mr-1.5" />Export Current
            </Button>}
            {can("sku:in_transit_export") && <Button variant="outline" onClick={() => downloadFile("/skus/in-transit-export.xlsx", `in-transit-${new Date().toISOString().slice(0,10)}.xlsx`)} data-testid="sku-transit-export-button" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Download className="w-4 h-4 mr-1.5" />Export In Transit
            </Button>}
            {can("sku:template") && <Button variant="outline" onClick={() => downloadFile("/skus/template.xlsx", "master-sku-template.xlsx")} data-testid="sku-template-button">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />Template
            </Button>}
            {can("sku:upload") && <label className="inline-flex">
              <input type="file" accept=".xlsx" className="hidden" data-testid="sku-xlsx-upload"
                onChange={e => { uploadUpsert(e.target.files?.[0]); e.target.value = ""; }} />
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium border border-slate-300 hover:bg-slate-100 cursor-pointer">
                <Upload className="w-4 h-4" /> Bulk Update .xlsx
              </span>
            </label>}
            <div className="relative inline-flex items-center gap-2 flex-wrap border-l border-slate-200 pl-2 ml-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Adjust</span>
              <Button variant="outline" size="sm" onClick={() => downloadFile("/skus/stock-adjust-template.xlsx", "stock-adjust-template.xlsx")} data-testid="stock-adjust-template">
                <Download className="w-3.5 h-3.5 mr-1" />Stock tpl
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx" className="hidden" data-testid="stock-adjust-upload"
                  onChange={e => { uploadStockAdjust(e.target.files?.[0]); e.target.value = ""; }} />
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-rose-600 hover:bg-rose-700 text-white cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Deduct Stock
                </span>
              </label>
              <Button variant="outline" size="sm" onClick={() => downloadFile("/skus/rack-adjust-template.xlsx", "rack-code-template.xlsx")} data-testid="rack-adjust-template">
                <Download className="w-3.5 h-3.5 mr-1" />Rack tpl
              </Button>
              <label className="inline-flex">
                <input type="file" accept=".xlsx" className="hidden" data-testid="rack-adjust-upload"
                  onChange={e => { uploadRackAdjust(e.target.files?.[0]); e.target.value = ""; }} />
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-800 hover:bg-slate-700 text-white cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Rack Update
                </span>
              </label>
            </div>
            <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="sku-paste-button">
                  <ClipboardPaste className="w-4 h-4 mr-1.5" />Paste from Excel
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle>Bulk import SKUs from Excel</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-slate-500">Copy columns from Excel and paste below. Columns (in order): <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">sku · name · description · rack_code · unit</span>. First row can be a header.</p>
                    <Button variant="outline" size="sm" onClick={() => downloadFile("/skus/template.xlsx", "master-sku-template.xlsx")} data-testid="sku-paste-template">
                      <Download className="w-3.5 h-3.5 mr-1" />Template
                    </Button>
                  </div>
                  <Textarea data-testid="sku-paste-textarea" rows={8} value={pasteText}
                    onChange={e=>setPasteText(e.target.value)}
                    placeholder={"sku\tname\tdescription\track_code\tunit\nSKU-001\tBlue Widget\tCotton\tA1-01\tpcs"} />
                  {previewRows.length > 0 && (
                    <div className="border border-slate-200 rounded max-h-64 overflow-auto">
                      <table className="excel-table w-full text-xs">
                        <thead><tr><th>SKU</th><th>Name</th><th>Description</th><th>Rack</th><th>Unit</th></tr></thead>
                        <tbody>
                          {previewRows.slice(0, 50).map((r, i) => (
                            <tr key={i}><td className="font-mono">{r.sku}</td><td>{r.name}</td><td>{r.description}</td><td>{r.rack_code}</td><td>{r.unit}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setPasteOpen(false)}>Cancel</Button>
                  <Button onClick={bulkImport} data-testid="sku-paste-import-button" className="bg-slate-900" disabled={!previewRows.length}>
                    Import {previewRows.length || ""} rows
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="sku-add-button" className="bg-sky-600 hover:bg-sky-700">
                  <Plus className="w-4 h-4 mr-1.5" />Add SKU
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="sku-form-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit SKU" : "Add SKU"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>SKU code *</Label><Input data-testid="sku-code-input" value={form.sku}
                    onChange={e=>setForm({...form, sku:e.target.value})} placeholder="e.g. SKU-001" /></div>
                  <div><Label>Item name *</Label><Input data-testid="sku-name-input" value={form.name}
                    onChange={e=>setForm({...form, name:e.target.value})} /></div>
                  <div><Label>Description</Label><Textarea data-testid="sku-desc-input" value={form.description}
                    onChange={e=>setForm({...form, description:e.target.value})} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Rack code</Label><Input data-testid="sku-rack-input" value={form.rack_code}
                      onChange={e=>setForm({...form, rack_code:e.target.value})} placeholder="e.g. A1-05" /></div>
                    <div><Label>Unit</Label><Input value={form.unit}
                      onChange={e=>setForm({...form, unit:e.target.value})} placeholder="pcs" /></div>
                  </div>
                  <div><Label>Image URL <span className="text-[10px] text-slate-400">(external, e.g. Shopee CDN — kept as-is)</span></Label>
                    <Input data-testid="sku-image-url-input" value={form.image_url || ""}
                      onChange={e=>setForm({...form, image_url:e.target.value})} placeholder="https://cf.shopee.com.my/file/..." />
                    {form.image_url && (
                      <div className="mt-2"><SkuImage url={form.image_url} alt={form.sku} size={64} /></div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>RRP <span className="text-[10px] text-slate-400">(selling price, Phase 2 listing)</span></Label>
                      <Input type="number" step="0.01" min="0" data-testid="sku-rrp-input"
                        value={form.rrp ?? ""} onChange={e=>setForm({...form, rrp: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                    <div><Label>Discount Price</Label>
                      <Input type="number" step="0.01" min="0" data-testid="sku-discount-input"
                        value={form.discount_price ?? ""} onChange={e=>setForm({...form, discount_price: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                  </div>
                  <div className="border-t border-slate-200 pt-3 space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Checkbox checked={!!form.is_packaging} onCheckedChange={v=>setForm({...form, is_packaging: !!v})} data-testid="sku-is-packaging" />
                      This is a Packaging SKU (box / flyer / sticker)
                    </label>
                    {form.is_packaging && (
                      <div className="pl-6 space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox checked={!!form.packaging_allocation} data-testid="sku-packaging-alloc"
                            onCheckedChange={v=>setForm({...form, packaging_allocation: !!v, packaging_product_sku_ids: v ? (form.packaging_product_sku_ids||[]) : []})} />
                          Packaging Cost Allocation — distribute cost to selected product SKUs
                        </label>
                        {form.packaging_allocation && (
                          <div>
                            <Label>Related product SKUs <span className="text-[10px] text-slate-400">(1 packaging unit = 1 product unit)</span></Label>
                            <Input value={pkgSearch} data-testid="sku-packaging-search" placeholder="Search SKU or name to add…"
                              onChange={e=>{ setPkgSearch(e.target.value); searchPkg(e.target.value); }} />
                            {pkgResults.length > 0 && (
                              <div className="border border-slate-200 rounded mt-1 max-h-40 overflow-auto">
                                {pkgResults.map(p => (
                                  <button type="button" key={p.id} data-testid={`sku-packaging-result-${p.sku}`}
                                    className="block w-full text-left px-2 py-1 text-xs hover:bg-slate-100 font-mono"
                                    onClick={()=>addPkgProduct(p)}>{p.sku} — {p.name}</button>
                                ))}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2" data-testid="sku-packaging-chips">
                              {(form.packaging_product_sku_ids||[]).map(pid => (
                                <span key={pid} className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 px-2 py-0.5 rounded font-mono">
                                  {pkgNames[pid] || pid.slice(0,8)}
                                  <button type="button" onClick={()=>removePkgProduct(pid)} className="text-sky-400 hover:text-rose-600">×</button>
                                </span>
                              ))}
                              {(form.packaging_product_sku_ids||[]).length === 0 && <span className="text-xs text-slate-400">No products linked yet</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div><Label>Height (cm)</Label><Input type="number" step="0.01" data-testid="sku-height-input"
                      value={form.height_cm ?? ""} onChange={e=>setForm({...form, height_cm: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                    <div><Label>Length (cm)</Label><Input type="number" step="0.01" data-testid="sku-length-input"
                      value={form.length_cm ?? ""} onChange={e=>setForm({...form, length_cm: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                    <div><Label>Width (cm)</Label><Input type="number" step="0.01" data-testid="sku-width-input"
                      value={form.width_cm ?? ""} onChange={e=>setForm({...form, width_cm: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                    <div><Label>Weight (kg)</Label><Input type="number" step="0.001" data-testid="sku-weight-input"
                      value={form.weight_kg ?? ""} onChange={e=>setForm({...form, weight_kg: e.target.value === "" ? null : parseFloat(e.target.value)})} placeholder="—" /></div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button onClick={save} data-testid="sku-save-button" className="bg-slate-900">{editing ? "Save" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>
          )}
        </div>
      </header>

      <Dialog open={bulkDeleteOpen} onOpenChange={(v)=>{ setBulkDeleteOpen(v); if(!v) setBulkDeletePw(""); }}>
        <DialogContent data-testid="sku-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete {selected.size} SKU{selected.size === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">This will permanently remove the selected SKUs from the master sheet. Related invoice history is not affected. Enter your admin password to confirm.</p>
            <div>
              <Label>Password</Label>
              <Input type="password" value={bulkDeletePw} onChange={e=>setBulkDeletePw(e.target.value)}
                data-testid="sku-bulk-delete-password" autoFocus
                onKeyDown={e => { if (e.key === "Enter") bulkDelete(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkDeleteOpen(false)}>Cancel</Button>
            <Button onClick={bulkDelete} data-testid="sku-bulk-delete-confirm" className="bg-rose-600 hover:bg-rose-700">
              <Trash2 className="w-4 h-4 mr-1.5" />Delete {selected.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDeleteXlsxOpen} onOpenChange={(v)=>{ setBulkDeleteXlsxOpen(v); if(!v){ setBulkDeleteXlsxPw(""); setBulkDeleteXlsxFile(null); } }}>
        <DialogContent data-testid="sku-bulk-delete-xlsx-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Bulk Delete SKUs via Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-600">Upload an Excel file with a single column named <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">sku</span> listing the SKU codes to delete. Related invoice history is not affected.</p>
              <Button variant="outline" size="sm" onClick={() => downloadFile("/skus/bulk-delete-template.xlsx", "sku-bulk-delete-template.xlsx")} data-testid="sku-bulk-delete-template">
                <Download className="w-3.5 h-3.5 mr-1" />Template
              </Button>
            </div>
            <div>
              <Label>Excel file</Label>
              <Input type="file" accept=".xlsx" data-testid="sku-bulk-delete-xlsx-file"
                onChange={e => setBulkDeleteXlsxFile(e.target.files?.[0] || null)} />
              {bulkDeleteXlsxFile && <p className="text-xs text-slate-500 mt-1">Selected: {bulkDeleteXlsxFile.name}</p>}
            </div>
            <div>
              <Label>Admin password</Label>
              <Input type="password" value={bulkDeleteXlsxPw} onChange={e=>setBulkDeleteXlsxPw(e.target.value)}
                data-testid="sku-bulk-delete-xlsx-password"
                onKeyDown={e => { if (e.key === "Enter") bulkDeleteXlsx(); }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkDeleteXlsxOpen(false)}>Cancel</Button>
            <Button onClick={bulkDeleteXlsx} data-testid="sku-bulk-delete-xlsx-confirm" className="bg-rose-600 hover:bg-rose-700" disabled={!bulkDeleteXlsxFile}>
              <Trash2 className="w-4 h-4 mr-1.5" />Delete From File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-2 mb-3">
        {["products", "packaging", ...(can("combo:view") ? ["combo"] : [])].map(t => (
          <button key={t} data-testid={`sku-tab-${t}`} onClick={()=>{ setSkuTab(t); setPage(1); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${skuTab===t?"bg-slate-900 text-white":"border border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {t === "products" ? "Products" : t === "packaging" ? "Packaging" : "Combo Code"}
          </button>
        ))}
      </div>
      {skuTab === "combo" ? <ComboCode can={can} /> : (<>
      <div className="flex items-center justify-between gap-3 mb-2 text-xs text-slate-500">
        <div>
          Showing <span className="font-semibold text-slate-800">{viewRows.length === 0 ? 0 : (page - 1) * pageSize + 1}</span>–<span className="font-semibold text-slate-800">{Math.min(page * pageSize, viewRows.length)}</span> of <span className="font-semibold text-slate-800">{viewRows.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          {[50, 100, 500].map(n => (
            <button key={n} data-testid={`sku-page-size-${n}`}
              onClick={()=>changePageSize(n)}
              className={`px-2.5 py-1 rounded font-mono ${pageSize === n ? "bg-slate-900 text-white" : "border border-slate-200 hover:bg-slate-100"}`}>
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="sku-table">
          <thead>
            <tr className="text-center">
              {can("sku:bulk_update") && (
                <th rowSpan={2} className="w-8 align-middle">
                  <Checkbox checked={pagedRows.length > 0 && pagedRows.every(r => selected.has(r.id))}
                    onCheckedChange={toggleAllSel} data-testid="sku-toggle-all" />
                </th>
              )}
              {showPhoto && <th rowSpan={2} className="w-16 align-middle">Photo</th>}
              <th rowSpan={2} className="align-middle text-center">SKU</th>
              <th rowSpan={2} className="align-middle text-center">Item Name</th>
              <th colSpan={2} className="text-center border-b border-slate-200">Rack Code</th>
              <th colSpan={3} className="text-center border-b border-slate-200">Stocks Quantity</th>
              {can("finance:view") && (<>
              <th rowSpan={2} className="text-center align-middle">CNY / Pc<br/><span className="text-[10px] normal-case font-normal text-slate-400">base</span></th>
              <th rowSpan={2} className="text-center align-middle">CNY / Pc<br/><span className="text-[10px] normal-case font-normal text-slate-400">after adj</span></th>
              <th rowSpan={2} className="text-center align-middle">Mo Avg RM<br/><span className="text-[10px] normal-case font-normal text-slate-400">ex freight</span></th>
              <th rowSpan={2} className="text-center align-middle">Mo Avg RM<br/><span className="text-[10px] normal-case font-normal text-slate-400">inc freight</span></th>
              </>)}
              {skuTab === "products" && can("finance:view") && (<>
                <th rowSpan={2} className="text-center align-middle">Packaging<br/><span className="text-[10px] normal-case font-normal text-slate-400">RM / pc</span></th>
                <th rowSpan={2} className="text-center align-middle">Total incl. Pkg<br/><span className="text-[10px] normal-case font-normal text-slate-400">RM / pc</span></th>
              </>)}
              {skuTab === "packaging" && (<>
                <th rowSpan={2} className="text-center align-middle">Allocate?</th>
                <th rowSpan={2} className="text-center align-middle">Linked</th>
                <th rowSpan={2} className="text-center align-middle">Avail</th>
                <th rowSpan={2} className="text-center align-middle">Used</th>
                <th rowSpan={2} className="text-center align-middle">Balance</th>
                {can("finance:view") && <th rowSpan={2} className="text-center align-middle">Pkg RM/pc</th>}
              </>)}
              {showDims && (
                <>
                  <th rowSpan={2} className="text-center align-middle">H<br/><span className="text-[10px] normal-case font-normal text-slate-400">cm</span></th>
                  <th rowSpan={2} className="text-center align-middle">L<br/><span className="text-[10px] normal-case font-normal text-slate-400">cm</span></th>
                  <th rowSpan={2} className="text-center align-middle">W<br/><span className="text-[10px] normal-case font-normal text-slate-400">cm</span></th>
                  <th rowSpan={2} className="text-center align-middle">Weight<br/><span className="text-[10px] normal-case font-normal text-slate-400">kg</span></th>
                </>
              )}
              <th rowSpan={2}></th>
              {(can("sku:edit_product") || can("print:labels")) && <th rowSpan={2} className="text-center align-middle">Actions</th>}
            </tr>
            <tr className="text-center">
              <th className="text-center">Standard</th>
              <th className="text-center">Balance</th>
              <th className="text-center">Warehouse</th>
              <th className="text-center">In Transit</th>
              <th className="text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map(r => (
              <Fragment key={r.id}>
              <tr data-testid={`sku-row-${r.sku}`}>
                {can("sku:bulk_update") && (
                  <td>
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={()=>toggleSel(r.id)}
                      data-testid={`sku-select-${r.sku}`} />
                  </td>
                )}
                {showPhoto && <td><SkuImage url={r.image_url} alt={r.sku} size={56} /></td>}
                <td className="font-mono font-semibold text-slate-800">{r.sku}</td>
                <td className="whitespace-nowrap">{r.name}</td>
                <td><span className="font-mono text-xs px-2 py-1 bg-slate-100 rounded">{r.rack_code || "—"}</span></td>
                <td>
                  <Input defaultValue={r.balance_stock_area || ""} data-testid={`sku-balance-${r.sku}`}
                    className="h-8 w-32 font-mono text-xs" placeholder="—"
                    onBlur={e => { if (e.target.value !== (r.balance_stock_area || "")) saveBalance(r.id, e.target.value); }} />
                </td>
                <td className="text-right font-mono font-semibold text-emerald-700" data-testid={`sku-current-${r.sku}`}>{r.current_stock ?? 0}</td>
                <td className="text-right font-mono text-amber-700" data-testid={`sku-transit-${r.sku}`}>
                  {(r.in_transit_stock ?? 0) > 0 && can("sku:in_transit_view") ? (
                    <button onClick={()=>openTransit(r)} data-testid={`sku-transit-btn-${r.sku}`}
                      className="underline decoration-dotted underline-offset-2 hover:text-amber-900 font-semibold cursor-pointer">
                      {r.in_transit_stock}
                    </button>
                  ) : 0}
                </td>
                <td className="text-right font-mono font-bold text-slate-900" data-testid={`sku-total-${r.sku}`}>{r.total_available ?? 0}</td>
                {can("finance:view") && (<>
                <td className="text-right cny-badge" data-testid={`sku-cny-base-${r.sku}`}>{r.latest_cny_base != null ? `¥${r.latest_cny_base}` : "—"}</td>
                <td className="text-right cny-badge font-semibold" data-testid={`sku-cny-full-${r.sku}`}>{r.latest_cny_full != null ? `¥${r.latest_cny_full}` : "—"}</td>
                <td className="text-right rm-badge" data-testid={`sku-fifo-ex-${r.sku}`}>{r.fifo_month_rm_ex_freight != null ? `RM${r.fifo_month_rm_ex_freight}` : "—"}</td>
                <td className="text-right rm-badge font-semibold" data-testid={`sku-fifo-inc-${r.sku}`}>{r.fifo_month_rm_inc_freight != null ? `RM${r.fifo_month_rm_inc_freight}` : "—"}</td>
                </>)}
                {skuTab === "products" && can("finance:view") && (<>
                  <td className="text-right rm-badge" data-testid={`sku-pkg-pc-${r.sku}`}>{r.packaging_alloc_per_pc != null ? `RM${r.packaging_alloc_per_pc}` : "—"}</td>
                  <td className="text-right rm-badge font-semibold" data-testid={`sku-total-pkg-${r.sku}`}>{r.total_inc_packaging_rm != null ? `RM${r.total_inc_packaging_rm}` : (r.fifo_month_rm_inc_freight != null ? `RM${r.fifo_month_rm_inc_freight}` : "—")}</td>
                </>)}
                {skuTab === "packaging" && (<>
                  <td className="text-center text-xs">{r.packaging_allocation ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="text-center font-mono text-xs" data-testid={`sku-pkg-linked-${r.sku}`}>{(r.packaging_product_sku_ids||[]).length || "—"}</td>
                  <td className="text-right font-mono text-xs">{r.packaging_available_qty ?? "—"}</td>
                  <td className="text-right font-mono text-xs">{r.packaging_allocated_qty ?? "—"}</td>
                  <td className="text-right font-mono text-xs font-semibold text-amber-700" data-testid={`sku-pkg-balance-${r.sku}`}>{r.packaging_remaining_qty ?? "—"}</td>
                  {can("finance:view") && <td className="text-right rm-badge">{r.packaging_cost_per_pc != null ? `RM${r.packaging_cost_per_pc}` : "—"}</td>}
                </>)}
                {showDims && (
                  <>
                    <td className="text-right font-mono text-xs" data-testid={`sku-height-${r.sku}`}>{r.height_cm ?? "—"}</td>
                    <td className="text-right font-mono text-xs" data-testid={`sku-length-${r.sku}`}>{r.length_cm ?? "—"}</td>
                    <td className="text-right font-mono text-xs" data-testid={`sku-width-${r.sku}`}>{r.width_cm ?? "—"}</td>
                    <td className="text-right font-mono text-xs" data-testid={`sku-weight-${r.sku}`}>{r.weight_kg ?? "—"}</td>
                  </>
                )}
                <td>
                  {r.description ? (
                    <Button variant="ghost" size="sm" data-testid={`sku-info-${r.sku}`}
                      onClick={()=>setExpanded({...expanded, [r.id]: !expanded[r.id]})}>
                      <Info className="w-4 h-4 mr-1" />{expanded[r.id] ? "Hide" : "Info"}
                    </Button>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                {(can("sku:edit_product") || can("print:labels")) && (
                  <td className="text-right">
                    {!r.is_packaging && can("print:labels") && (
                      <Button variant="ghost" size="sm" onClick={()=>setLabelSku(r)} data-testid={`sku-print-label-${r.sku}`} title="Print label sticker"><Printer className="w-4 h-4" /></Button>
                    )}
                    {can("sku:edit_product") && <Button variant="ghost" size="sm" onClick={()=>openEdit(r)} data-testid={`sku-edit-${r.sku}`}><Pencil className="w-4 h-4" /></Button>}
                    {can("sku:edit_product") && <Button variant="ghost" size="sm" onClick={()=>del(r.id)} data-testid={`sku-delete-${r.sku}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </td>
                )}
              </tr>
              {expanded[r.id] && r.description && (
                <tr className="bg-slate-50">
                  <td colSpan={(showPhoto ? 1 : 0) + (showDims ? 4 : 0) + (user?.role === "admin" ? 14 : 12)} className="text-slate-600 text-sm">
                    <span className="caption mr-3">Description</span>{r.description}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={(showPhoto ? 1 : 0) + (showDims ? 4 : 0) + (user?.role === "admin" ? 14 : 13)} className="text-center py-12 text-slate-400">No SKUs yet. Add your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-3 text-xs">
          <button data-testid="sku-page-prev" disabled={page === 1}
            onClick={()=>setPage(p => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
            ← Prev
          </button>
          <span className="px-3 py-1.5 font-mono">
            Page <span className="font-semibold text-slate-900">{page}</span> of <span className="font-semibold text-slate-900">{totalPages}</span>
          </span>
          <button data-testid="sku-page-next" disabled={page === totalPages}
            onClick={()=>setPage(p => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">
            Next →
          </button>
        </div>
      )}
      </>)}
      <SkuLabelPrintDialog open={!!labelSku} onOpenChange={v => !v && setLabelSku(null)} sku={labelSku} />

      <Dialog open={!!transitDrill} onOpenChange={v => !v && setTransitDrill(null)}>
        <DialogContent className="max-w-3xl" data-testid="transit-drill-dialog">
          <DialogHeader>
            <DialogTitle>In Transit · <span className="font-mono">{transitDrill?.sku}</span></DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-500 -mt-2">{transitDrill?.name}</div>
          <div className="border border-slate-200 rounded max-h-[60vh] overflow-auto">
            <table className="excel-table w-full text-xs" data-testid="transit-drill-table">
              <thead><tr>
                <th>Invoice</th><th>Order ID</th><th className="text-right">Qty</th>
                <th>China Tracking</th><th>Warehouse Code</th><th>Stage</th><th className="text-right">CBM</th>
              </tr></thead>
              <tbody>
                {(transitDrill?.items || []).map(it => (
                  <tr key={it.id} data-testid={`transit-drill-row-${it.id}`}>
                    <td className="font-mono">{it.invoice_no || <span className="text-amber-600">— pending —</span>}</td>
                    <td className="font-mono">{it.order_id || "—"}</td>
                    <td className="text-right font-mono font-semibold">{it.quantity}</td>
                    <td className="font-mono">{it.china_tracking_code || "—"}</td>
                    <td className="font-mono">{it.warehouse_code || "—"}</td>
                    <td><span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700">{it.stage}</span></td>
                    <td className="text-right font-mono">{it.cbm ?? "—"}</td>
                  </tr>
                ))}
                {transitDrill?.loading && (<tr><td colSpan={7} className="text-center py-6 text-slate-400">Loading…</td></tr>)}
                {!transitDrill?.loading && (transitDrill?.items || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-6 text-slate-400">No in-transit lines.</td></tr>
                )}
              </tbody>
              {(transitDrill?.items || []).length > 0 && (
                <tfoot><tr className="font-semibold bg-slate-50">
                  <td colSpan={2} className="text-right">Total In Transit</td>
                  <td className="text-right font-mono text-amber-700" data-testid="transit-drill-total">{transitDrill?.total_qty}</td>
                  <td colSpan={4}></td>
                </tr></tfoot>
              )}
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
